import React, { useCallback, useMemo } from 'react';
import { NuvoMultiSelectDropDown } from '@nuvolo/nuux-core/components/NuvoDropDown';
import dxDropDownBox from 'devextreme/ui/drop_down_box';
import { NativeEventInfo } from 'devextreme/events';
import { ValueChangedInfo } from 'devextreme/ui/editor/editor';
import { FilterGroupItem, FilterValue } from '../ListProps';
import { FilterChip } from '../util';
import FilterGroupProps from './FilterGroupProps';
import { DocumentNode } from 'graphql';
import { useApolloClient } from '@apollo/react-hooks';
import { createDataSource } from '#/common/utils/createDataSource';

export type ReferenceDataSource = {
  getItem: DocumentNode;
  getItemList: DocumentNode;
  displayExpr: string;
  valueExpr: string;
};

export type ReferenceDsType = {
  type: 'reference';
  dataSource: ReferenceDataSource;
};

export type ReferenceFilterGroupProps = FilterGroupProps<ReferenceDsType>;

const ReferenceFilterGroup: React.FC<ReferenceFilterGroupProps> = ({
  group,
  filters,
  onFilterChange,
}) => {
  const { dataField, dataSource } = group;

  const valueExpr = dataSource.valueExpr;
  const displayExpr = dataSource.displayExpr;

  const selectedValuesForGroup = filters.map((f) => f.value);

  const gqlClient = useApolloClient();

  const ds = useMemo(() => {
    return createDataSource<any>(gqlClient, {
      ...dataSource,
      cacheEnabled: true,
      searchKey: displayExpr,
    });
  }, [dataSource, displayExpr]);

  const onSelect = useCallback((e: NativeEventInfo<dxDropDownBox, Event> & ValueChangedInfo) => {
    const selectedValues = (e.value as FilterValue[]) || [];
    const selectedItems = (e.component.option('selectedItems') as Record<string, unknown>[]) || [];

    const newFilters = selectedValues.map<FilterChip>((v) => {
      const match = selectedItems.find((item) => item[valueExpr as string] === v);
      const label = (match?.[displayExpr as string] as string | undefined) ?? String(v);
      const customColors = match?.customColors as FilterChip['customColors'];

      return {
        dataField: dataField,
        value: v,
        label,
        customColors,
      };
    });

    onFilterChange(newFilters);
  }, []);

  return (
    <>
      <div className="filter-panel-section-title">{group.label}</div>

      <NuvoMultiSelectDropDown
        className="mobile-list-multiselect-dropdown"
        key={`${group.id}-${JSON.stringify(selectedValuesForGroup)}`}
        dataSource={ds}
        valueExpr={valueExpr}
        displayExpr={displayExpr}
        style={{ zIndex: 9999 }}
        searchEnabled
        showSelectionControls
        placeholder={`Select ${group.label}`}
        stylingMode="outlined"
        multiline
        showClearButton
        defaultValue={selectedValuesForGroup}
        onValueChanged={onSelect}
      />
    </>
  );
};

export default ReferenceFilterGroup;
